/**
 * PPO — generalized advantage estimation and the clipped surrogate update.
 *
 * Three things in here are where PPO implementations actually go wrong, so they
 * are called out rather than buried:
 *
 * 1. TERMINAL HANDLING IN GAE. When a step ends an episode, the value of the
 *    next state is not a prediction about the future — there is no future. The
 *    bootstrap term and the recursive accumulator must BOTH be zeroed at that
 *    step. Get only the bootstrap and the advantage still leaks backwards
 *    across the boundary through lambda; get neither and every episode's return
 *    is contaminated by the next one's. The symptom is subtle: training works,
 *    just worse, and worse in a way that looks like a hyperparameter problem.
 *    `computeGae` zeroes both, and the tests assert that perturbing a reward
 *    after a terminal changes no advantage before it.
 *
 * 2. THE RATIO USES THE BEHAVIOUR POLICY'S LOG-PROBABILITY. `Transition.logProb`
 *    is recorded at acting time and never recomputed. Recomputing it from the
 *    current parameters makes the ratio identically 1 on the first epoch and
 *    turns the clip into a no-op.
 *
 * 3. THE CLIPPED OBJECTIVE IS A MINIMUM, NOT A CLAMP. `min(r*A, clip(r)*A)`
 *    is a pessimistic bound: it removes the incentive to move the ratio further
 *    in the improving direction past the boundary, but keeps the full gradient
 *    when the ratio has moved the WRONG way, so a bad step can always be
 *    undone. Clamping the ratio instead would zero the gradient on both sides
 *    and strand the policy outside the trust region.
 *
 * Diagnostics come back from every update because they are the earliest warning
 * a run has gone wrong: entropy sliding to zero means the policy collapsed
 * before it learned anything, and an approximate KL an order of magnitude above
 * the usual 0.01-0.02 means the step size is too large for the data.
 */

import { Rng } from '../physics/rng';
import { clipGradNorm, entropyOfLogProbs, logSoftmax, MlpPolicy, softmax } from './nn';
import type { Adam } from './nn';
import type {
  Batch,
  ForwardResult,
  PPOConfig,
  ReturnScaleState,
  Transition,
} from './types';

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Defaults, and why each one is what it is.
 *
 * gamma 0.99          At 120 Hz a discount of 0.99 has a half-life of 69 ticks,
 *                     about half a second, which is far too short for a game
 *                     where a shot's payoff arrives seconds later. Training
 *                     therefore runs on decimated steps (one decision every
 *                     8 physics ticks, 15 Hz), where 0.99 is a ~4.6 s half-life
 *                     and a ~100 step horizon. The value here assumes that
 *                     decimation; raising the decision rate means raising
 *                     gamma to match, not leaving it alone.
 * lambda 0.95         The usual bias/variance compromise. lambda 1 is the
 *                     unbiased Monte-Carlo return with the full variance of the
 *                     episode; lambda 0 is the one-step TD error, low variance
 *                     and badly biased by an untrained critic.
 * clipRange 0.2       The value from the PPO paper, and the one every reference
 *                     implementation still ships.
 * entropyCoef 0.01    The main lever against premature collapse. Too low and
 *                     the policy commits to whatever it found first; too high
 *                     and it never commits at all.
 * valueCoef 0.5       Balances a squared-error term against a log-ratio term
 *                     that lives on a much smaller scale.
 * maxGradNorm 0.5     Bounds the damage a single anomalous minibatch can do.
 * learningRate 3e-4   Adam's standard starting point for policy gradients.
 * epochs 4            Reusing a batch more than a handful of times pushes the
 *                     data further off-policy than the clip range can excuse.
 * minibatchSize 256   Large enough that per-minibatch advantage normalization
 *                     estimates a meaningful mean and standard deviation.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LEARNING RATE IS 1e-3 AND NOT ADAM'S USUAL 3e-4
 * ---------------------------------------------------------------------------
 *
 * 3e-4 is the number every PPO paper quotes, and it is quoted for batches an
 * order of magnitude larger than the ones this trainer runs on a laptop. Every
 * update here is only `epochs * stepsPerIteration / minibatchSize` optimizer
 * steps — 64 of them at the 4,096-step default — so the distance the policy
 * moves per iteration is small in a way the learning rate alone does not say.
 * Measured on this environment at 4,096 steps: 3e-4 gives an approximate KL of
 * 0.003-0.008, four times below the 0.01-0.03 band a healthy run sits in, and
 * the curve is flat for as long as anyone is willing to watch. 1e-3 puts it at
 * 0.02-0.04 with entropy falling steadily from 4.13 to 2.8 over 200 iterations
 * — committing, not collapsing. 3e-3 was measured too, and is worse than
 * either: entropy falls just as fast and the touch rate goes DOWN, which is
 * what a step size past the trust region looks like.
 *
 * This is a BATCH SIZE compensation, not a claim that 3e-4 is wrong. Raise
 * `stepsPerIteration` by 10x and 3e-4 is the right number again.
 *
 * `entropyCoef` drops from 0.01 to 0.005 for the same reason. The action table
 * has 62 entries, so a uniform policy carries 4.13 nats, and at 0.01 the bonus
 * is a meaningful fraction of an advantage signal this small — enough to hold a
 * policy near uniform for hundreds of iterations. 0.005 still keeps entropy well
 * off the floor (2.8 nats, about 16 effective actions, after 200 iterations)
 * while letting the policy commit inside a few minutes.
 */
export const DEFAULT_PPO_CONFIG: PPOConfig = {
  gamma: 0.99,
  lambda: 0.95,
  clipRange: 0.2,
  entropyCoef: 0.005,
  valueCoef: 0.5,
  maxGradNorm: 0.5,
  learningRate: 1e-3,
  epochs: 4,
  minibatchSize: 256,
  normalizeReturns: true,
};

// ---------------------------------------------------------------------------
// Return scale
// ---------------------------------------------------------------------------

/** Floor on the estimated standard deviation, so the first iterations — when
 *  the estimate is built from a handful of near-identical returns — cannot
 *  multiply the rewards by a thousand. */
const RETURN_STD_EPS = 1e-4;

/**
 * A running estimate of the standard deviation of the DISCOUNTED return, used
 * to divide the rewards before GAE. This is SB3's `VecNormalize(norm_reward=…)`
 * and it exists for one reason: to stop the reward function's UNITS from being
 * a hyperparameter.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DOES AND DOES NOT FIX
 * ---------------------------------------------------------------------------
 *
 * It does NOT fix the policy gradient's scale. `computeLossAndGradients` already
 * standardizes advantages inside every minibatch, and Adam is invariant to a
 * constant rescaling of the whole gradient, so the actor's step size is already
 * independent of the reward units. That was measured before this class was
 * written: multiplying every reward weight by 100 left the approximate KL and
 * the learning curve unchanged to within noise. Anyone diagnosing "tiny returns
 * therefore tiny gradients" should check that first — it is not what is
 * happening.
 *
 * What it DOES fix is the balance between the two heads. The value loss is
 * `0.5*(V - R)^2`, which is quadratic in the reward units, and it is summed with
 * the policy loss BEFORE Adam sees it. With returns around 0.1 the critic
 * contributes about 1e-2 of the gradient the actor contributes, so `valueCoef`
 * means nothing and the critic — whose predictions are the baseline every
 * advantage is measured against — trains far more slowly than it should. With
 * returns standardized to unit variance, `valueCoef: 0.5` is the ratio it claims
 * to be, and it stays that ratio when someone edits a reward weight in the UI.
 *
 * The mean is deliberately NOT subtracted. Centering rewards changes the
 * optimal policy whenever episodes can end early — a constant added to every
 * step's reward is worth more to a long episode than to a short one — and this
 * environment ends on a goal.
 *
 * Deterministic and clock-free; the state is checkpointed so a resumed run
 * divides by the same number the straight run did.
 */
export class ReturnNormalizer {
  /** Running mean and (population) variance of the discounted-return statistic,
   *  by Chan's parallel form so a whole trajectory is folded in at once. */
  private meanValue = 0;
  /** 1 rather than 0 so `scale` is usable before anything has been observed. */
  private varianceValue = 1;
  /** Zero, so the FIRST fold reproduces its batch's mean and variance exactly
   *  rather than blending them with a prior nothing measured. `observe` returns
   *  early on an empty batch, so the divisor below is never zero. */
  private countValue = 0;

  get mean(): number {
    return this.meanValue;
  }

  get variance(): number {
    return this.varianceValue;
  }

  get count(): number {
    return this.countValue;
  }

  /** What the rewards are divided by. */
  get scale(): number {
    return Math.sqrt(this.varianceValue) + RETURN_STD_EPS;
  }

  /**
   * Fold one trajectory in. `rewards` and `dones` are the RAW, unscaled values,
   * oldest first; the accumulator restarts at every terminal, so a trajectory
   * that spans several episodes is still described correctly.
   */
  observe(
    rewards: ArrayLike<number>,
    dones: ArrayLike<boolean>,
    gamma: number,
    priorAccumulator = 0
  ): number {
    let accumulator = priorAccumulator;
    let sum = 0;
    let sumSquares = 0;
    const n = rewards.length;
    if (n === 0) return accumulator;
    for (let i = 0; i < n; i++) {
      accumulator = accumulator * gamma + rewards[i];
      sum += accumulator;
      sumSquares += accumulator * accumulator;
      if (dones[i]) accumulator = 0;
    }

    const batchMean = sum / n;
    const batchVariance = Math.max(0, sumSquares / n - batchMean * batchMean);
    const delta = batchMean - this.meanValue;
    const total = this.countValue + n;
    const m2 =
      this.varianceValue * this.countValue +
      batchVariance * n +
      (delta * delta * this.countValue * n) / total;
    this.meanValue += (delta * n) / total;
    this.varianceValue = m2 / total;
    this.countValue = total;
    return accumulator;
  }

  /** JSON-safe snapshot, for a checkpoint. */
  exportState(): ReturnScaleState {
    return { mean: this.meanValue, variance: this.varianceValue, count: this.countValue };
  }

  importState(state: ReturnScaleState): void {
    this.meanValue = state.mean;
    this.varianceValue = state.variance;
    this.countValue = state.count;
  }
}

// ---------------------------------------------------------------------------
// GAE
// ---------------------------------------------------------------------------

/**
 * Generalized advantage estimation, backwards over a trajectory.
 *
 *   delta_t = r_t + gamma * V(s_{t+1}) * (1 - done_t) - V(s_t)
 *   A_t     = delta_t + gamma * lambda * (1 - done_t) * A_{t+1}
 *   R_t     = A_t + V(s_t)
 *
 * `bootstrapValue` is V of the state AFTER the last transition, used only when
 * the last transition is not terminal — a rollout cut off mid-episode still
 * knows the episode continues, and dropping the bootstrap there would teach the
 * critic that every rollout boundary is worth zero.
 *
 * Returns are advantage + value rather than a separately accumulated discounted
 * sum, which is what makes the critic's target consistent with the advantage
 * the actor is being pushed by. Computing them independently is a classic way
 * to end up with an actor and a critic optimizing subtly different objectives.
 */
export const computeGae = (
  rewards: ArrayLike<number>,
  values: ArrayLike<number>,
  dones: ArrayLike<boolean>,
  bootstrapValue: number,
  gamma: number,
  lambda: number,
  outAdvantages: Float64Array,
  outReturns: Float64Array,
): void => {
  const n = rewards.length;
  if (values.length !== n || dones.length !== n) {
    throw new Error(`GAE inputs disagree: ${n} rewards, ${values.length} values, ${dones.length} dones`);
  }
  let running = 0;
  for (let t = n - 1; t >= 0; t--) {
    // Zero on a terminal step kills the bootstrap AND the recursion in one
    // move; that single factor is the whole of correct terminal handling.
    const continues = dones[t] ? 0 : 1;
    const nextValue = t === n - 1 ? bootstrapValue : values[t + 1];
    const delta = rewards[t] + gamma * nextValue * continues - values[t];
    running = delta + gamma * lambda * continues * running;
    outAdvantages[t] = running;
    outReturns[t] = running + values[t];
  }
};

/**
 * Pack a list of transitions into the flat `Batch` the update loop consumes,
 * computing advantages and returns on the way.
 *
 * `rewardScale` divides every reward before GAE — see `ReturnNormalizer`. It
 * multiplies rather than divides here so the common case is 1 and costs one
 * multiply; the caller inverts. Values are NOT rescaled: they come from a
 * critic that was already trained on scaled returns, so they are already in
 * those units.
 */
export const buildBatch = (
  transitions: readonly Transition[],
  bootstrapValue: number,
  obsSize: number,
  gamma: number,
  lambda: number,
  rewardScale = 1,
): Batch => {
  const count = transitions.length;
  const obs = new Float32Array(count * obsSize);
  const actions = new Int32Array(count);
  const logProbs = new Float64Array(count);
  const oldValues = new Float64Array(count);
  const rewards = new Float64Array(count);
  const dones: boolean[] = new Array(count);

  for (let i = 0; i < count; i++) {
    const t = transitions[i];
    if (t.obs.length !== obsSize) {
      throw new Error(`transition ${i} has obs length ${t.obs.length}, expected ${obsSize}`);
    }
    obs.set(t.obs, i * obsSize);
    actions[i] = t.action;
    logProbs[i] = t.logProb;
    oldValues[i] = t.value;
    rewards[i] = t.reward * rewardScale;
    dones[i] = t.done;
  }

  const advantages = new Float64Array(count);
  const returns = new Float64Array(count);
  computeGae(rewards, oldValues, dones, bootstrapValue, gamma, lambda, advantages, returns);

  return { obs, actions, logProbs, advantages, returns, oldValues, count };
};

// ---------------------------------------------------------------------------
// The clipped surrogate, in isolation
// ---------------------------------------------------------------------------

/** One sample's surrogate objective and its derivative w.r.t. the ratio. */
export interface SurrogateTerm {
  /** The objective being MAXIMIZED: min(r*A, clip(r)*A). */
  objective: number;
  /** d(objective)/d(ratio). Zero exactly when the clipped branch is binding. */
  dObjectiveDRatio: number;
  /** True when the clip changed the answer, for the clip-fraction diagnostic. */
  clipped: boolean;
}

/**
 * `min(r*A, clip(r, 1-e, 1+e)*A)` and its gradient.
 *
 * The gradient is zero only when the clipped branch is selected AND the ratio
 * is genuinely outside the range: for a ratio inside the range the two branches
 * are equal and the gradient is A either way, so a naive "if clipped then zero"
 * would silently zero the gradient of half the batch at the boundary.
 */
export const surrogate = (ratio: number, advantage: number, clipRange: number): SurrogateTerm => {
  const lo = 1 - clipRange;
  const hi = 1 + clipRange;
  const unclipped = ratio * advantage;
  const clampedRatio = ratio < lo ? lo : ratio > hi ? hi : ratio;
  const clipped = clampedRatio * advantage;
  if (unclipped <= clipped) {
    return { objective: unclipped, dObjectiveDRatio: advantage, clipped: false };
  }
  const inRange = ratio >= lo && ratio <= hi;
  return {
    objective: clipped,
    dObjectiveDRatio: inRange ? advantage : 0,
    clipped: !inRange,
  };
};

// ---------------------------------------------------------------------------
// Losses
// ---------------------------------------------------------------------------

/** Per-term weights on the scalar that gets differentiated. Exposed so a test
 *  can isolate one term: a bug in the value gradient is invisible when only the
 *  sum of all three is checked. */
export interface LossCoefficients {
  policy: number;
  value: number;
  entropy: number;
}

export interface LossReport {
  /** The exact scalar differentiated:
   *  `policy*policyLoss + value*valueLoss - entropy*meanEntropy`. */
  total: number;
  /** Mean of -min(r*A, clip(r)*A). Lower is better; its absolute value means
   *  little on its own because the advantages are renormalized every batch. */
  policyLoss: number;
  /** Mean of 0.5*(V - R)^2. */
  valueLoss: number;
  /** Mean policy entropy in nats. log(actionCount) is uniform. */
  entropy: number;
  /** Schulman's k3 estimator of KL(old || new). */
  approxKl: number;
  /** Fraction of samples whose ratio the clip actually bound. */
  clipFraction: number;
}

export interface PPOOptions {
  /**
   * Clip the value function's update to `clipRange` around the old value, the
   * way the original PPO code does. Off by default, matching the modern
   * reference implementations: the trick was never ablated in the paper, and it
   * mostly slows the critic down. On when a run's value loss is spiking.
   */
  clipValueLoss?: boolean;
  /** Seed for minibatch shuffling. Part of what makes a run reproducible. */
  seed?: number;
}

/** Guard for the per-minibatch advantage normalization. */
const ADVANTAGE_EPS = 1e-8;

// ---------------------------------------------------------------------------
// Sharding a minibatch
// ---------------------------------------------------------------------------

/**
 * The advantage standardization a minibatch is scored against, hoisted out of
 * `computeLossAndGradients` so that SEVERAL evaluators can be handed the same
 * numbers.
 *
 * `count` is the size of the WHOLE minibatch, not of the slice being evaluated.
 * Every per-sample term is divided by it, so a shard's partial sums add up to
 * exactly the sums the whole minibatch would have produced — which is what
 * makes summing shard reports meaningful rather than an average of averages.
 */
export interface AdvantageNorm {
  mean: number;
  /** 1 / (population standard deviation + eps). */
  invStd: number;
  /** Samples in the full minibatch. */
  count: number;
}

/**
 * Mean and inverse standard deviation of `indices`' advantages.
 *
 * Identical arithmetic, in identical order, to the inline version this was
 * extracted from — the serial path calls it with the same indices it always
 * used, so nothing about a default run changes.
 */
export const advantageNorm = (batch: Batch, indices: ArrayLike<number>): AdvantageNorm => {
  const n = indices.length;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += batch.advantages[indices[i]];
  mean /= n;
  let variance = 0;
  for (let i = 0; i < n; i++) {
    const d = batch.advantages[indices[i]] - mean;
    variance += d * d;
  }
  return { mean, invStd: 1 / (Math.sqrt(variance / n) + ADVANTAGE_EPS), count: n };
};

/**
 * Where shard `index` of `pieces` starts and ends inside a run of `count`
 * samples. Largest remainders first, the same rule ../rl/trainer's `splitSteps`
 * uses for rollout chunks.
 *
 * A PURE FUNCTION OF (count, pieces, index), and nothing else. That is the
 * whole of what makes the parallel gradient path reproducible: the boundaries
 * do not depend on how many workers happened to be free, on which one answered
 * first, or on anything else measured at run time. Change the piece count and
 * the arithmetic changes — see `PPO.updateParallel`.
 */
export const shardRange = (
  count: number,
  pieces: number,
  index: number
): { start: number; end: number } => {
  const base = Math.floor(count / pieces);
  const extra = count - base * pieces;
  const start = index * base + Math.min(index, extra);
  return { start, end: start + base + (index < extra ? 1 : 0) };
};

/** A report that contributes nothing, for a shard that was given no samples. */
export const zeroLossReport = (): LossReport => ({
  total: 0,
  policyLoss: 0,
  valueLoss: 0,
  entropy: 0,
  approxKl: 0,
  clipFraction: 0,
});

/** Fold `from` into `into`, term by term. Every field of a shard's report is a
 *  sum already divided by the FULL minibatch size, so addition is the whole of
 *  the reduction. */
export const addLossReport = (into: LossReport, from: LossReport): LossReport => {
  into.total += from.total;
  into.policyLoss += from.policyLoss;
  into.valueLoss += from.valueLoss;
  into.entropy += from.entropy;
  into.approxKl += from.approxKl;
  into.clipFraction += from.clipFraction;
  return into;
};

/**
 * Somewhere else that can evaluate part of a minibatch.
 *
 * The seam `PPO.updateParallel` is written against, so ./ppo knows nothing
 * about worker threads, shared memory or message ports — ./gradients supplies
 * both an in-process reference implementation and the one that talks to the
 * rollout pool, exactly as `RolloutBackend` splits collection.
 *
 * THE CALLER OWNS SHARD 0. A minibatch is split into `workerShards + 1` pieces
 * and the main thread computes the first of them itself: it is blocked waiting
 * on the others regardless, so leaving it idle wastes one core out of every
 * `workerShards + 1`.
 */
export interface GradientShards {
  /** Pieces handed out. Fixed for the life of the session, because the shard
   *  boundaries — and therefore the arithmetic — depend on it. */
  readonly workerShards: number;
  /** Publish a batch and the hyperparameters it is scored under. Called once
   *  per PPO update. */
  begin(batch: Batch, spec: GradientSessionSpec): Promise<void>;
  /** Publish this epoch's index permutation. */
  publishOrder(order: Int32Array): void;
  /** Start shards 1..n on `order[start, end)` at `params`. Must post the work
   *  BEFORE it returns, so the caller's own shard overlaps with theirs. */
  dispatch(params: Float64Array, start: number, end: number, norm: AdvantageNorm): Promise<void>;
  /** Add the finished shards' gradients into `out` IN SHARD-INDEX ORDER and
   *  return their summed report. Never in completion order: floating-point
   *  addition is not associative, so a race for first place would be a race for
   *  the last bits of every parameter. */
  reduce(out: Float64Array): LossReport;
}

/** What a shard needs to score a batch the same way the caller would. */
export interface GradientSessionSpec {
  ppo: PPOConfig;
  clipValueLoss: boolean;
  /** Length of the parameter vector, so the session can size its buffers. */
  paramCount: number;
  /** The trunk shape, so a shard can build the same network. Sent rather than
   *  assumed: a session whose shards had a different layout would produce a
   *  gradient of a different function and nothing would say so. */
  hiddenSizes: readonly number[];
}

export class PPO {
  readonly config: PPOConfig;
  readonly clipValueLoss: boolean;

  private readonly net: MlpPolicy;
  private readonly adam: Adam;
  private readonly rng: Rng;

  /** Scratch, reused across every sample so the update loop allocates nothing
   *  per step. */
  private readonly forwardOut: ForwardResult;
  private readonly logProbs: Float64Array;
  private readonly probs: Float64Array;
  private readonly dLogits: Float64Array;
  /** Index permutation, grown on demand and reused across epochs. */
  private scratchOrder: Int32Array | null = null;

  constructor(net: MlpPolicy, adam: Adam, config: PPOConfig, options: PPOOptions = {}) {
    if (adam.size !== net.parameterCount) {
      throw new Error(
        `optimizer sized ${adam.size} for a network with ${net.parameterCount} parameters`,
      );
    }
    this.net = net;
    this.adam = adam;
    this.config = config;
    this.clipValueLoss = options.clipValueLoss ?? false;
    this.rng = new Rng(options.seed ?? 0);
    adam.learningRate = config.learningRate;

    this.forwardOut = { logits: new Float64Array(net.actionCount), value: 0 };
    this.logProbs = new Float64Array(net.actionCount);
    this.probs = new Float64Array(net.actionCount);
    this.dLogits = new Float64Array(net.actionCount);
  }

  /**
   * Loss and (optionally) gradients for one minibatch.
   *
   * Advantages are normalized WITHIN the minibatch: PPO's step size is set by
   * the clip range, which is a bound on the ratio, so the advantage's scale is
   * what decides how far a step actually moves. Leaving it unnormalized makes
   * the effective learning rate track the reward function's units, and every
   * reward-weight change silently becomes a learning-rate change. Normalizing
   * per minibatch rather than per batch is the reference behaviour.
   *
   * Gradients ACCUMULATE into the network's buffer; the caller zeroes.
   *
   * `norm` overrides the standardization that would otherwise be measured from
   * `indices`. It exists for sharding: a shard sees a SLICE of the minibatch,
   * and a slice's own mean and standard deviation are not the minibatch's, so
   * evaluating one without being told the whole batch's numbers would optimize
   * a different objective. Omitted — the default and every serial call — the
   * behaviour is exactly what it always was.
   */
  computeLossAndGradients(
    batch: Batch,
    indices: ArrayLike<number>,
    coefs: LossCoefficients,
    withGradients: boolean,
    norm?: AdvantageNorm,
  ): LossReport {
    const n = indices.length;
    if (n === 0) throw new Error('empty minibatch');
    const net = this.net;
    const obsSize = net.obsSize;
    const actionCount = net.actionCount;
    const clipRange = this.config.clipRange;

    // Mean and (population) standard deviation of this minibatch's advantages.
    // A single-sample minibatch has zero spread, so every advantage becomes 0
    // and the policy term contributes nothing. That is correct, not a bug — one
    // sample carries no information about which action was better than average.
    const stats = norm ?? advantageNorm(batch, indices);
    const mean = stats.mean;
    const invStd = stats.invStd;

    let policyLoss = 0;
    let valueLoss = 0;
    let entropySum = 0;
    let klSum = 0;
    let clipCount = 0;
    // The FULL minibatch's size, which for an unsharded call is `n`. See
    // `AdvantageNorm`.
    const invN = 1 / stats.count;

    for (let i = 0; i < n; i++) {
      const idx = indices[i];
      net.forwardAt(batch.obs, idx * obsSize, this.forwardOut);
      logSoftmax(this.forwardOut.logits, this.logProbs);
      softmax(this.forwardOut.logits, this.probs);

      const action = batch.actions[idx];
      const newLogProb = this.logProbs[action];
      const logRatio = newLogProb - batch.logProbs[idx];
      const ratio = Math.exp(logRatio);
      const advantage = (batch.advantages[idx] - mean) * invStd;

      const term = surrogate(ratio, advantage, clipRange);
      policyLoss -= term.objective * invN;
      if (term.clipped) clipCount++;

      // Schulman's k3 estimator: exp(x) - 1 - x with x = log(new/old). Unlike
      // the naive mean of -logRatio it is never negative and has far lower
      // variance, so an early-stopping threshold on it means something.
      klSum += Math.exp(logRatio) - 1 - logRatio;

      const entropy = entropyOfLogProbs(this.logProbs);
      entropySum += entropy;

      const value = this.forwardOut.value;
      const target = batch.returns[idx];
      let vDiff = value - target;
      // 1 when the value gradient flows through the raw prediction, 0 when the
      // clipped branch is binding and the prediction is detached from the loss.
      let vGate = 1;
      if (this.clipValueLoss) {
        const old = batch.oldValues[idx];
        const delta = value - old;
        const clampedDelta = delta < -clipRange ? -clipRange : delta > clipRange ? clipRange : delta;
        const clippedDiff = old + clampedDelta - target;
        if (clippedDiff * clippedDiff > vDiff * vDiff) {
          vDiff = clippedDiff;
          vGate = delta === clampedDelta ? 1 : 0;
        }
      }
      valueLoss += 0.5 * vDiff * vDiff * invN;

      if (withGradients) {
        // Policy: newLogProb = z_a - logSumExp(z), so
        // d(newLogProb)/dz_j = [j == a] - p_j, and d(ratio)/d(newLogProb) = r.
        const dLossDLogProb = -term.dObjectiveDRatio * ratio * invN * coefs.policy;
        // Entropy: dH/dz_k = -p_k * (log p_k + H). The loss carries -coef*H.
        const entropyScale = coefs.entropy * invN;
        for (let j = 0; j < actionCount; j++) {
          const p = this.probs[j];
          let d = dLossDLogProb * ((j === action ? 1 : 0) - p);
          d += entropyScale * p * (this.logProbs[j] + entropy);
          this.dLogits[j] = d;
        }
        const dValue = coefs.value * vGate * vDiff * invN;
        net.backward(this.dLogits, dValue);
      }
    }

    const entropy = entropySum * invN;
    return {
      total: coefs.policy * policyLoss + coefs.value * valueLoss - coefs.entropy * entropy,
      policyLoss,
      valueLoss,
      entropy,
      approxKl: klSum * invN,
      clipFraction: clipCount * invN,
    };
  }

  /**
   * One PPO update: `epochs` shuffled passes over `batch` in minibatches.
   *
   * Diagnostics are averaged over the minibatches, weighted by sample count so
   * a short trailing minibatch does not count as much as a full one.
   */
  update(batch: Batch): LossReport {
    if (batch.count === 0) throw new Error('cannot update on an empty batch');
    const order = this.indexBuffer(batch.count);
    const coefs: LossCoefficients = {
      policy: 1,
      value: this.config.valueCoef,
      entropy: this.config.entropyCoef,
    };
    const size = Math.max(1, Math.min(this.config.minibatchSize, batch.count));

    let total = 0;
    let policyLoss = 0;
    let valueLoss = 0;
    let entropy = 0;
    let approxKl = 0;
    let clipFraction = 0;
    let weight = 0;

    for (let epoch = 0; epoch < this.config.epochs; epoch++) {
      this.shuffle(order);
      for (let start = 0; start < batch.count; start += size) {
        const end = Math.min(start + size, batch.count);
        const slice = order.subarray(start, end);
        this.net.zeroGradients();
        const report = this.computeLossAndGradients(batch, slice, coefs, true);
        clipGradNorm(this.net.getGradients(), this.config.maxGradNorm);
        this.adam.update(this.net.getParameters(), this.net.getGradients());

        const w = end - start;
        total += report.total * w;
        policyLoss += report.policyLoss * w;
        valueLoss += report.valueLoss * w;
        entropy += report.entropy * w;
        approxKl += report.approxKl * w;
        clipFraction += report.clipFraction * w;
        weight += w;
      }
    }

    const inv = 1 / weight;
    return {
      total: total * inv,
      policyLoss: policyLoss * inv,
      valueLoss: valueLoss * inv,
      entropy: entropy * inv,
      approxKl: approxKl * inv,
      clipFraction: clipFraction * inv,
    };
  }

  /**
   * `update`, with every minibatch's GRADIENT computed in parallel.
   *
   * ===========================================================================
   * WHAT IS PARALLEL AND WHAT IS NOT
   * ===========================================================================
   *
   * Only the inner sum. A PPO update is a strictly SEQUENTIAL chain of optimizer
   * steps — minibatch k+1 is evaluated at the parameters minibatch k produced —
   * so there is no legitimate way to run two minibatches at once, and anything
   * that appears to is computing gradients at stale parameters. What IS
   * data-parallel is one minibatch: its loss is a mean over independent samples,
   * so the samples can be split, summed separately, and added up.
   *
   * The loop below is therefore `update`'s loop, unchanged in structure and in
   * every decision it makes: the same shuffle from the same generator, the same
   * minibatch boundaries, the same advantage standardization measured over the
   * same full minibatch, the same clip, the same Adam step. What changes is who
   * evaluates the samples.
   *
   * ===========================================================================
   * THIS IS NOT BIT-IDENTICAL TO `update`, AND CANNOT BE
   * ===========================================================================
   *
   * A sum of doubles is not associative. `update` accumulates one running total
   * per parameter over the whole minibatch in shuffled-index order; this
   * accumulates `workerShards + 1` partial totals and adds those. The values
   * differ in the last bits, and 64 sequential Adam steps per iteration amplify
   * that a little. `test/rl/gradients.test.ts` pins the disagreement at a
   * relative error below 1e-9 per parameter after a full update, which is the
   * honest form of the claim; anyone who needs bit-identity — the checkpoint
   * resume test, the worker-versus-sync trajectory test — must use `update`,
   * and the trainer's default does.
   *
   * What this path DOES promise is reproducibility with ITSELF: fixed shard
   * boundaries (`shardRange`), a reduction that always walks shards in index
   * order, and no other run-time input, so the same seed and the same shard
   * count give the same parameters every time.
   */
  async updateParallel(batch: Batch, shards: GradientShards): Promise<LossReport> {
    if (batch.count === 0) throw new Error('cannot update on an empty batch');
    const order = this.indexBuffer(batch.count);
    const coefs: LossCoefficients = {
      policy: 1,
      value: this.config.valueCoef,
      entropy: this.config.entropyCoef,
    };
    const size = Math.max(1, Math.min(this.config.minibatchSize, batch.count));
    const pieces = shards.workerShards + 1;
    await shards.begin(batch, {
      ppo: this.config,
      clipValueLoss: this.clipValueLoss,
      paramCount: this.net.parameterCount,
      hiddenSizes: this.net.hiddenSizes,
    });

    let total = 0;
    let policyLoss = 0;
    let valueLoss = 0;
    let entropy = 0;
    let approxKl = 0;
    let clipFraction = 0;
    let weight = 0;

    for (let epoch = 0; epoch < this.config.epochs; epoch++) {
      this.shuffle(order);
      shards.publishOrder(order);
      for (let start = 0; start < batch.count; start += size) {
        const end = Math.min(start + size, batch.count);
        // Measured HERE, over the whole minibatch, and handed down. A shard
        // that standardized its own slice would be optimizing a different
        // objective — see `AdvantageNorm`.
        const norm = advantageNorm(batch, order.subarray(start, end));

        // Posted before the local shard runs, so the workers and this thread
        // are busy at the same time. `dispatch` is contractually required to
        // have sent the work by the time it returns its promise.
        const pending = shards.dispatch(this.net.getParameters(), start, end, norm);

        const own = shardRange(end - start, pieces, 0);
        this.net.zeroGradients();
        const report =
          own.end > own.start
            ? this.computeLossAndGradients(
                batch,
                order.subarray(start + own.start, start + own.end),
                coefs,
                true,
                norm,
              )
            : zeroLossReport();

        await pending;
        addLossReport(report, shards.reduce(this.net.getGradients()));

        clipGradNorm(this.net.getGradients(), this.config.maxGradNorm);
        this.adam.update(this.net.getParameters(), this.net.getGradients());

        const w = end - start;
        total += report.total * w;
        policyLoss += report.policyLoss * w;
        valueLoss += report.valueLoss * w;
        entropy += report.entropy * w;
        approxKl += report.approxKl * w;
        clipFraction += report.clipFraction * w;
        weight += w;
      }
    }

    const inv = 1 / weight;
    return {
      total: total * inv,
      policyLoss: policyLoss * inv,
      valueLoss: valueLoss * inv,
      entropy: entropy * inv,
      approxKl: approxKl * inv,
      clipFraction: clipFraction * inv,
    };
  }

  /** Index permutation buffer, grown on demand and reused thereafter. */
  private indexBuffer(count: number): Int32Array {
    const cached = this.scratchOrder;
    if (cached && cached.length === count) return cached;
    const fresh = new Int32Array(count);
    for (let i = 0; i < count; i++) fresh[i] = i;
    this.scratchOrder = fresh;
    return fresh;
  }

  /** Fisher-Yates from the seeded generator. */
  private shuffle(indices: Int32Array): void {
    for (let i = indices.length - 1; i > 0; i--) {
      const j = this.rng.nextInt(i + 1);
      const tmp = indices[i];
      indices[i] = indices[j];
      indices[j] = tmp;
    }
  }
}
